import { FlowComponent } from '../../../flow.min.js';
import styles from './styles.js';

const template = FlowComponent.html`
    <div class="mb-4">
        <label class="block text-sm font-medium text-gray-700 mb-1">${'label'}</label>
        <div class="relative">
            <textarea
                name="${'name'}"
                rows="4"
                maxlength="${'maxlength'}"
                placeholder="${'placeholder'}"
                class="appearance-none rounded-lg relative block w-full px-3 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm resize-none"
            >${'value'}</textarea>
            <div class="absolute bottom-2 right-2 text-xs text-gray-400 pointer-events-none bg-white/80 px-1 rounded">
                <span id="counter">0</span>/${'maxlength'}
            </div>
        </div>
    </div>
`;

export class FlowTextarea extends FlowComponent {
    static formAssociated = true;
    #internals;

    constructor() {
        super();
        this.#internals = this.attachInternals();
    }

    static get styles() { return styles; }
    static get template() { return template; }

    onMount() {
        super.onMount();
        const textarea = this.root.querySelector('textarea');
        const counter = this.root.getElementById('counter');
        const maxLen = this.getAttribute('maxlength') || 175;

        const updateState = () => {
            const val = textarea.value;
            this.setAttribute('value', val); // Keep attribute in sync
            this.#internals.setFormValue(val);
            
            // Update counter
            if (counter) {
                counter.textContent = val.length;
                // Visual warning when close to limit
                if (val.length >= maxLen) {
                    counter.parentElement.classList.add('text-red-500', 'font-bold');
                } else {
                    counter.parentElement.classList.remove('text-red-500', 'font-bold');
                }
            }
            
            // Sync validity
            if (!textarea.checkValidity()) {
                this.#internals.setValidity(
                    textarea.validity,
                    textarea.validationMessage,
                    textarea
                );
            } else {
                this.#internals.setValidity({});
            }
        };

        textarea.addEventListener('input', updateState);
        
        // Initial sync
        // If attribute is set but textarea is empty (first render), sync it.
        // If value property was set before mount, textarea should reflect it.
        const initialVal = this.getAttribute('value') || '';
        if (textarea.value !== initialVal) {
             textarea.value = initialVal;
        }
        updateState();
    }

    formResetCallback() {
        const initialValue = this.getAttribute('value') || '';
        this.value = initialValue;
        this.#internals.setFormValue(initialValue);
        // Trigger input event to update counter and validity logic
        const textarea = this.root.querySelector('textarea');
        if (textarea) textarea.dispatchEvent(new Event('input'));
    }

    get value() {
        return this.root.querySelector('textarea')?.value || this.getAttribute('value') || '';
    }

    set value(val) {
        const textarea = this.root.querySelector('textarea');
        if (textarea) {
            textarea.value = val;
            textarea.dispatchEvent(new Event('input')); // Trigger updateState
        } else {
            this.setAttribute('value', val);
        }
    }
}

customElements.define('flow-textarea', FlowTextarea);
